import { useCallback, useState } from 'react';
import { Container, Button, Dropdown } from 'react-bootstrap';
import { AppLoader } from '../../../../Components/common/AppLoader/AppLoader';
import { HospitalCard } from '../../../../Components/ui/HospitalCard/HospitalCard';
import { usePartnerHospitals } from '../../../../hooks/usePartnerHospitals';
import { useAdminDashboardCtx } from '../../hooks/AdminDashboardContext';
import {
  uploadEstablishmentBanner,
  uploadEstablishmentLogo,
} from './adminListUploadApi';
import '../../../home/partnerhospitals/partnerhospitals.css';
import './admin-list-views.css';

export const AdminHospitalListScreen = () => {
  const { selectStep } = useAdminDashboardCtx();
  const {
    countries,
    locationTabs,
    selectedCountry,
    activeLocation,
    setActiveLocation,
    handleCountrySelect,
    filteredHospitals,
    filteredEstablishments,
    refreshEstablishments,
    loading,
    error,
  } = usePartnerHospitals({ noDefaults: true });

  const [uploading, setUploading] = useState({});
  const [uploadError, setUploadError] = useState('');

  const setUploadState = (id, field, value) => {
    setUploading((prev) => ({ ...prev, [`${id}-${field}`]: value }));
  };

  const handleUpload = useCallback(async (establishmentId, field, file, uploadFn) => {
    const establishment = filteredEstablishments.find(
      (est) => String(est.establishment_id) === String(establishmentId),
    );
    if (!establishment) return;

    setUploadError('');
    setUploadState(establishmentId, field, true);
    try {
      await uploadFn(establishment, file);
      await refreshEstablishments();
    } catch (err) {
      setUploadError(err.message || 'Failed to upload image.');
    } finally {
      setUploadState(establishmentId, field, false);
    }
  }, [filteredEstablishments, refreshEstablishments]);

  return (
    <div className="admin-list-view">
      <section className="partner-hospitals">
        <Container fluid>
          <div className="partner-hospitals__header-grid">
            <div className="partner-hospitals__intro">
              <h2 className="partner-hospitals__title">Hospital List</h2>
              <p className="partner-hospitals__subtitle">
                Upload banner and logo images for establishments shown on the public website.
              </p>
            </div>

            <Button
              type="button"
              className="partner-hospitals__manage"
              onClick={() => selectStep(37)}
            >
              Manage Establishments
            </Button>

            <div className="partner-hospitals__filter-row">
              <div className="partner-hospitals__tabs-wrap">
                <div className="partner-hospitals__tabs">
                  {locationTabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      className={`partner-hospitals__tab ${activeLocation?.id === tab.id ? 'partner-hospitals__tab--active' : ''}`}
                      onClick={() => setActiveLocation(tab)}
                    >
                      {tab.name}
                    </button>
                  ))}
                </div>
              </div>

              <Dropdown
                className="partner-hospitals__country-dropdown"
                onSelect={handleCountrySelect}
              >
                <Dropdown.Toggle className="partner-hospitals__country-toggle">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                  </svg>
                  {selectedCountry?.name || 'Select Country'}
                </Dropdown.Toggle>
                <Dropdown.Menu className="partner-hospitals__country-menu">
                  {countries.map((country) => (
                    <Dropdown.Item
                      key={country.id}
                      eventKey={String(country.id)}
                      active={selectedCountry?.id === country.id}
                    >
                      {country.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          {uploadError && (
            <p className="text-danger mb-3">{uploadError}</p>
          )}

          {loading && <AppLoader className="py-5" />}

          {!loading && error && (
            <p className="text-center py-4 text-danger">{error}</p>
          )}

          {!loading && !error && (
            <div className="partner-hospitals__scroll-wrap">
              <div className="partner-hospitals__track">
                {filteredHospitals.length > 0 ? (
                  filteredHospitals.map((h) => (
                    <div key={h.id} className="partner-hospitals__card-item">
                      <HospitalCard
                        {...h}
                        hideActions
                        allowImageUpload
                        uploadingBanner={uploading[`${h.id}-banner`]}
                        uploadingLogo={uploading[`${h.id}-logo`]}
                        onUploadBanner={(file) => handleUpload(h.id, 'banner', file, uploadEstablishmentBanner)}
                        onUploadLogo={(file) => handleUpload(h.id, 'logo', file, uploadEstablishmentLogo)}
                      />
                    </div>
                  ))
                ) : (
                  <p className="partner-hospitals__empty">
                    No hospitals found. Add establishments in Establishment Master.
                  </p>
                )}
              </div>
            </div>
          )}
        </Container>
      </section>
    </div>
  );
};

export default AdminHospitalListScreen;
